import { useCallback, useEffect, useRef, useState } from "react";
import {
  type FieldId,
  type GameAction,
  type GameState,
  type ObsMessage,
  type ObsStatus,
  regraftState,
  type StateMessage,
} from "../../shared/types";
import { WS_REVOKED, wsUrl } from "./api";
import { useTournament } from "./tournament";

const PING_MS = 25_000;
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 8000;
/** How long a ping may go unanswered before the socket is declared dead. The
 *  server auto-answers "ping" with "pong" without even waking the DO, so
 *  anything longer than a round trip plus congestion headroom means the path
 *  is gone whatever `readyState` claims. Measured from the PING, never from
 *  the interval: a background tab runs timers about once a minute, and
 *  judging silence against the 25 s cadence declared every throttled socket
 *  dead on each tick - a reconnect a minute, per tab, all day, ~3k Durable
 *  Object connects a day for nothing. */
const PONG_GRACE_MS = 6_000;

export interface LiveGame {
  state: GameState | null;
  /** serverNow - clientNow at last message; add to Date.now() for server time. */
  skew: number;
  connected: boolean;
  /** OBS bridge status for this field, when a bridge has reported. */
  obs: ObsStatus | null;
  /** Entries on the SERVER's undo stack, from the last state message. The
   *  stack lives in the DO, so this is the only honest source; undefined
   *  against an old server (the UI falls back to its heuristic then). */
  undoDepth: number | undefined;
  /** The action the next server-side undo would revert; undefined when the
   *  stack is empty or the server predates the field. */
  undoKind: GameAction["type"] | undefined;
  /**
   * Ask the field's bridge tab to push its OBS status again. Rides this
   * socket as one fixed string, so it costs no Worker request, no session
   * resolve and no audit line: as a POST it wrote one audit entry every five
   * seconds for as long as the stream panel was open. A no-op while the
   * socket is down, and silently ignored by the server on a socket that did
   * not earn the admin tag.
   */
  nudgeObs: () => void;
}

/**
 * WebSocket subscription to one field's game state, with reconnect/backoff,
 * keepalive pings, and clock-skew estimation. Read-only: writes go over HTTP.
 *
 * `admin: true` connects with role=admin: the DO then also pushes OBS bridge
 * status, which public overlay/board sockets never receive. The role is only
 * an intent: the server grants it from the session cookie on the upgrade
 * request, and a credential that no longer covers this field silently
 * downgrades to a plain client, so the score keeps flowing either way.
 */
export function useGameState(
  field: FieldId,
  opts?: { admin?: boolean; onRevoked?: () => void; src?: "overlay" | "board" },
): LiveGame {
  const { id: tournamentId } = useTournament();
  const admin = opts?.admin === true;
  // For the traffic counters only: which public surface this connect is
  // (overlay vs board look identical to the server otherwise). Read once -
  // callers pass a literal, and the socket URL must not depend on renders.
  const src = opts?.src;
  // Held in a ref, NOT a dependency. Callers pass an inline arrow, so putting
  // it in the effect's deps tore the socket down and rebuilt it on every
  // render: a connect/close loop that hammered the DO and never delivered a
  // close code to anyone.
  const onRevoked = opts?.onRevoked;
  const onRevokedRef = useRef(onRevoked);
  onRevokedRef.current = onRevoked;
  const [state, setState] = useState<GameState | null>(null);
  const [connected, setConnected] = useState(false);
  const [obs, setObs] = useState<ObsStatus | null>(null);
  const skewRef = useRef(0);
  const [skew, setSkew] = useState(0);
  const [undoDepth, setUndoDepth] = useState<number | undefined>(undefined);
  const [undoKind, setUndoKind] = useState<GameAction["type"] | undefined>(undefined);
  /** The live socket, for `nudgeObs`. A ref, so the callback is stable and a
   *  caller's interval does not rebuild on every reconnect. */
  const wsRef = useRef<WebSocket | null>(null);
  const nudgeObs = useCallback((): void => {
    const s = wsRef.current;
    if (s?.readyState === WebSocket.OPEN) s.send("obs-refresh");
  }, []);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let closed = false;
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let pingTimer: ReturnType<typeof setInterval> | undefined;
    // When ANYTHING last arrived, pongs included. Liveness used to rest on
    // TCP alone: an access point that silently stops passing traffic leaves
    // `readyState === OPEN` and `send()` succeeding into the void, so the
    // board sat on a stale score under a "live" badge for however long the OS
    // took to notice. The pings were already being sent — nothing checked
    // that anything came back.
    let lastHeardAt = Date.now();
    // When the last ping went out; 0 until one has. Alive means a message
    // (any message) arrived after it.
    let lastPingAt = 0;
    let probeTimer: ReturnType<typeof setTimeout> | undefined;
    const unanswered = (): boolean =>
      lastPingAt > lastHeardAt && Date.now() - lastPingAt > PONG_GRACE_MS;
    const ping = (): void => {
      if (ws?.readyState !== WebSocket.OPEN) return;
      lastPingAt = Date.now();
      ws.send("ping");
    };

    // A brand-new subscription knows nothing yet: field changes must not
    // carry the previous field's game (the version guard below would then
    // refuse the NEW field's frames until its `v` caught up) — nor its skew.
    setState(null);
    setObs(null);
    setUndoDepth(undefined);
    setUndoKind(undefined);
    skewRef.current = 0;
    setSkew(0);

    const socketUrl = (): string =>
      admin ? wsUrl(tournamentId, field, "admin") : wsUrl(tournamentId, field, undefined, src);

    const connect = (): void => {
      if (closed) return;
      ws = new WebSocket(socketUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        attempt = 0;
        lastHeardAt = Date.now();
        lastPingAt = 0;
        setConnected(true);
      };

      ws.onmessage = (ev: MessageEvent) => {
        lastHeardAt = Date.now();
        if (typeof ev.data !== "string" || ev.data === "pong") return;
        try {
          const msg = JSON.parse(ev.data) as StateMessage | ObsMessage;
          if (msg.type === "state") {
            const newSkew = msg.now - Date.now();
            skewRef.current = newSkew;
            setSkew(newSkew);
            if (msg.undo !== undefined) {
              setUndoDepth(msg.undo);
              // Read beside `undo`, not on its own: absence means an EMPTY
              // stack on a server that sends depths, so it must clear rather
              // than hold the label of an entry that was just popped.
              setUndoKind(msg.undoKind);
            }
            setState((prev) => {
              if (prev !== null && prev.v > msg.state.v) return prev;
              // Updates arrive without the team assets — see `slimMessage` in
              // field-do.ts. They were sent whole on connect, arrive whole
              // again on `setup`, and `sync` below asks for a whole one; so the
              // previous state is where they are. With no previous state there
              // is nothing to graft from, which renders a crestless team rather
              // than nothing at all.
              return msg.slim === true && prev !== null ? regraftState(msg.state, prev) : msg.state;
            });
          } else if (msg.type === "obs") {
            setObs(msg.status);
          }
        } catch {
          // Ignore malformed frames.
        }
      };

      ws.onclose = (ev: CloseEvent) => {
        setConnected(false);
        if (closed) return;
        // The grant behind this socket was revoked. Reconnecting would loop
        // forever against a credential that is gone; the gate above re-checks
        // the session and tells the volunteer what happened.
        if (ev.code === WS_REVOKED) {
          onRevokedRef.current?.();
          return;
        }
        attempt += 1;
        // Jittered: a FieldDO restart disconnects every subscriber of the
        // field at the same instant, and a deterministic curve had the whole
        // crowd re-upgrading in synchronized waves every 8 s.
        const base = Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * 2 ** Math.min(attempt, 3));
        reconnectTimer = setTimeout(connect, base * (0.5 + Math.random()));
      };

      ws.onerror = () => {
        ws?.close();
      };
    };

    connect();

    pingTimer = setInterval(() => {
      if (ws?.readyState !== WebSocket.OPEN) return;
      if (unanswered()) {
        // The last ping got no pong and nothing else arrived either: the
        // socket is dead whatever TCP still believes. Closing hands recovery
        // to the ordinary backoff path.
        ws.close();
        return;
      }
      ping();
    }, PING_MS);

    const onVisible = (): void => {
      if (document.visibilityState !== "visible") return;
      if (ws?.readyState === WebSocket.OPEN) {
        if (unanswered()) {
          // A phone out of a pocket with a silently-dead socket: `sync` would
          // just be sent into the same void. Reconnect instead, promptly.
          attempt = 0;
          ws.close();
          return;
        }
        // Answers with a FULL frame, which is also this client's way back if it
        // ever ends up holding a state with no assets on it. A ping rides
        // along and is checked on its own short timer: a socket that died
        // while the tab slept (no ping was pending, so `unanswered` cannot
        // know) is caught within the grace window, not at the next interval.
        ping();
        ws.send("sync");
        clearTimeout(probeTimer);
        probeTimer = setTimeout(() => {
          if (ws?.readyState === WebSocket.OPEN && unanswered()) {
            attempt = 0;
            ws.close();
          }
        }, PONG_GRACE_MS + 500);
      } else if (ws?.readyState !== WebSocket.CONNECTING) {
        clearTimeout(reconnectTimer);
        connect();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      clearTimeout(probeTimer);
      clearInterval(pingTimer);
      document.removeEventListener("visibilitychange", onVisible);
      ws?.close();
      wsRef.current = null;
    };
  }, [tournamentId, field, admin, src]);

  return { state, skew, connected, obs, undoDepth, undoKind, nudgeObs };
}

/** Re-render tick for clock displays. 200ms keeps the seconds digit crisp. */
export function useNow(skew: number, everyMs = 200): number {
  const [now, setNow] = useState(() => Date.now() + skew);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now() + skew), everyMs);
    return () => clearInterval(t);
  }, [skew, everyMs]);
  return now;
}
